import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { supabase } from '@/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { migrateAllUserData, migrateTemplateData } from '@/lib/dataMigration'
import { toast } from 'sonner'
import { logError } from '@/lib/sentry'
import { getSignupSource } from '@/lib/signupSource'
import { useLoginModal } from '@/contexts/LoginModalContext'

interface SignupFormProps {
  onComplete: () => void
}

// 채널 플랫폼 옵션
const CHANNEL_PLATFORMS = [
  { value: 'tiktok', label: '틱톡' },
  { value: 'youtube', label: '유튜브' },
  { value: 'band', label: '밴드' },
  { value: 'instagram', label: '인스타그램' },
  { value: 'other', label: '기타' },
] as const

type ChannelPlatform = typeof CHANNEL_PLATFORMS[number]['value']

// 8자리 랜덤 초대 코드 생성 함수
function generateReferralCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

export function SignupForm({ onComplete }: SignupFormProps) {
  const [nickname, setNickname] = useState('')
  const [referralCode, setReferralCode] = useState('')
  const [channelPlatform, setChannelPlatform] = useState<ChannelPlatform | ''>('')
  const [channelLink, setChannelLink] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { user, pseudoUserId } = useAuth()
  const { setShowReferralWelcomeModal } = useLoginModal()

  // 페이지 로드 시 sessionStorage에서 초대 코드 불러오기
  useEffect(() => {
    const savedReferralCode = sessionStorage.getItem('referralCode')
    if (savedReferralCode) {
      setReferralCode(savedReferralCode)
      console.log('[SignupForm] 저장된 초대 코드 불러옴:', savedReferralCode)
    }
  }, [])

  // 초대 코드가 입력되었는지 여부
  const hasReferralCode = referralCode.trim().length > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nickname.trim()) {
      toast.error('닉네임을 입력해주세요.')
      return
    }

    // 초대 코드가 있으면 채널 정보 필수
    if (hasReferralCode) {
      if (!channelPlatform) {
        toast.error('채널 플랫폼을 선택해주세요.')
        return
      }
      if (!channelLink.trim()) {
        toast.error('채널 링크/닉네임을 입력해주세요.')
        return
      }
    }

    setIsLoading(true)
    try {
      // pseudoUserId 로깅, 현재 회원 정보 로깅
      console.log('[SignupForm] pseudoUserId:', pseudoUserId);
      console.log('[SignupForm] user:', user);
      const trimmedNickname = nickname.trim()

      // ========================================
      // 1. 초대 코드 검증 (회원가입 전에 먼저 수행)
      // ========================================
      let validatedReferralData: { id: number; user_id: string } | null = null
      
      if (hasReferralCode && channelPlatform && channelLink.trim()) {
        const trimmedReferralCode = referralCode.trim().toUpperCase()
        console.log('[SignupForm] 초대 코드 검증 시작:', trimmedReferralCode)

        // 초대 코드로 추천인 찾기
        const { data: referralCodeData, error: referralCodeError } = await supabase
          .from('referral_code')
          .select('id, user_id')
          .ilike('code', trimmedReferralCode)
          .single()

        if (referralCodeError || !referralCodeData) {
          console.warn('[SignupForm] 유효하지 않은 초대 코드:', trimmedReferralCode)
          toast.error('유효하지 않은 초대 코드입니다. 초대 코드를 확인해주세요.')
          setIsLoading(false)
          return
        }

        // 본인 코드 체크는 user.id가 있을 때만 (가입 후에 다시 체크)
        validatedReferralData = referralCodeData
        console.log('[SignupForm] 초대 코드 검증 완료:', validatedReferralData)
      }

      // ========================================
      // 2. member 테이블에 데이터 추가
      // ========================================
      const { error: memberError } = await supabase
        .from('member')
        .insert({
          user_id: user?.id,
          nickname: trimmedNickname,
          signup_source: getSignupSource(),
        })

      if (memberError) {
        if (memberError.code === '23505') { // unique constraint violation
          toast.error('이미 사용 중인 닉네임입니다.')
          setIsLoading(false)
          return
        }
        throw memberError
      }

      // GA 가입 전환. 채널별 분해는 GA '첫 사용자 소스'(UTM 링크) 기준
      (window as { gtag?: (...args: unknown[]) => void }).gtag?.('event', 'sign_up', { method: user?.app_metadata?.provider })

      // ========================================
      // 3. 사용자 메타데이터 업데이트
      // ========================================
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          nickname: trimmedNickname
        }
      })

      if (updateError) throw updateError

      // ========================================
      // 4. 신규 회원의 초대 코드 생성 & referral 관계 생성
      // ========================================
      if (user?.id) {
        // 4-1. 신규 회원 초대 코드 생성
        try {
          let newReferralCode = generateReferralCode()
          let codeExists = true
          let attempts = 0
          const maxAttempts = 10

          // 고유한 코드가 생성될 때까지 반복
          while (codeExists && attempts < maxAttempts) {
            const { data: existingCode } = await supabase
              .from('referral_code')
              .select('id')
              .ilike('code', newReferralCode)
              .single()

            if (!existingCode) {
              codeExists = false
            } else {
              newReferralCode = generateReferralCode()
              attempts++
            }
          }

          // 초대 코드 저장
          const { error: codeError } = await supabase
            .from('referral_code')
            .insert({
              user_id: user.id,
              code: newReferralCode,
            })

          if (codeError) {
            console.error('[SignupForm] 초대 코드 생성 실패:', codeError)
          } else {
            console.log('[SignupForm] 초대 코드 생성 완료:', newReferralCode)
          }
        } catch (codeGenError) {
          console.error('[SignupForm] 초대 코드 생성 중 오류:', codeGenError)
        }

        // 4-2. 초대 코드로 가입한 경우, referral 관계 생성
        if (validatedReferralData && channelPlatform && channelLink.trim()) {
          try {
            // 본인 코드인지 다시 체크
            if (validatedReferralData.user_id === user.id) {
              console.warn('[SignupForm] 본인 초대 코드 사용 시도')
              toast.error('본인의 초대 코드는 사용할 수 없습니다.')
            } else {
              // 현재 이벤트 기간인지 확인
              const { data: activeEvent } = await supabase
                .from('referral_event')
                .select('id, name')
                .eq('is_active', true)
                .lte('start_at', new Date().toISOString())
                .gte('end_at', new Date().toISOString())
                .single()

              // referral 관계 생성
              const { error: referralError } = await supabase
                .from('referral')
                .insert({
                  referrer_id: validatedReferralData.user_id,
                  referee_id: user.id,
                  referral_code_id: validatedReferralData.id,
                  channel_platform: channelPlatform,
                  channel_link: channelLink.trim(),
                  is_event_period: !!activeEvent,
                  event_id: activeEvent?.id || null,
                })

              if (referralError) {
                if (referralError.code === '23505') {
                  console.warn('[SignupForm] 이미 추천 관계가 존재합니다.')
                } else {
                  console.error('[SignupForm] referral 생성 실패:', referralError)
                }
              } else {
                console.log('[SignupForm] referral 관계 생성 완료')
                toast.success('초대 코드가 적용되었습니다!')
              }
            }

            // 초대 코드 사용 후 sessionStorage에서 제거
            sessionStorage.removeItem('referralCode')
          } catch (referralError) {
            console.error('[SignupForm] referral 처리 중 오류:', referralError)
          }
        }
      }
      
      // 비회원 데이터 마이그레이션 및 DB 동기화
      if (user?.id) {
        try {
          // toast.loading('데이터를 이전하는 중입니다...', { id: 'migration' });
          
          // 마이그레이션을 위한 pseudoUserIds 수집
          const currentPseudoUserIdFromStorage = sessionStorage.getItem("pseudoUserId");
          // console.log(`[SignupForm] 현재 pseudoUserId: ${pseudoUserId}`);
          // console.log(`[SignupForm] sessionStorage에서 가져온 pseudoUserId: ${currentPseudoUserIdFromStorage}`);
          
          const pseudoUserIdsToMigrate: string[] = [];
          
          // 우선순위 1: 컨텍스트의 pseudoUserId
          if (pseudoUserId && pseudoUserId.trim() !== '') {
            pseudoUserIdsToMigrate.push(pseudoUserId);
          }
          
          // 우선순위 2: sessionStorage의 pseudoUserId
          if (currentPseudoUserIdFromStorage && 
              currentPseudoUserIdFromStorage.trim() !== '' && 
              !pseudoUserIdsToMigrate.includes(currentPseudoUserIdFromStorage)) {
            pseudoUserIdsToMigrate.push(currentPseudoUserIdFromStorage);
          }
          
          // 추가적인 pseudoUserIds를 customer-templates 키에서 찾기
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('customer-templates-')) {
              const extractedId = key.replace('customer-templates-', '');
              if (extractedId !== user.id && 
                  extractedId.trim() !== '' && 
                  !pseudoUserIdsToMigrate.includes(extractedId)) {
                pseudoUserIdsToMigrate.push(extractedId);
              }
            }
          }
          
          console.log(`[SignupForm] 마이그레이션할 모든 pseudoUserIds:`, pseudoUserIdsToMigrate);
          
          // 마이그레이션할 pseudoUserId가 없으면 바로 완료
          if (pseudoUserIdsToMigrate.length === 0) {
            console.log(`[SignupForm] 마이그레이션할 pseudoUserId가 없습니다.`);
            toast.success('회원가입이 완료되었습니다.', { id: 'migration' });
            onComplete();
            return;
          }

          // *** 중요: NewExcel.tsx에 마이그레이션 진행 중임을 알림 ***
          localStorage.setItem('migrationFlow', 'in_progress');
          
          // 마이그레이션할 날짜 찾기
          let focusDate = null;
          for (const pUserId of pseudoUserIdsToMigrate) {
            const highlightedDatesKey = `highlighted_dates_${pUserId}`;
            const savedDatesJson = sessionStorage.getItem(highlightedDatesKey);
            
            if (savedDatesJson) {
              try {
                const dates = JSON.parse(savedDatesJson) as string[];
                if (dates.length > 0) {
                  // 날짜 내림차순 정렬하여 가장 최근 날짜 선택
                  dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
                  focusDate = dates[0];
                  localStorage.setItem('migrationFocusDate', focusDate);
                  console.log(`[SignupForm] 마이그레이션 포커스 날짜 설정: ${focusDate}`);
                  break; // 첫 번째 발견된 날짜 셋을 사용
                }
              } catch (e) {
                console.error(`[SignupForm] highlighted_dates 파싱 오류:`, e);
              }
            }
          }
          
          try {
            // 실제 마이그레이션 실행 (모든 작업이 완료될 때까지 대기)
            // 1. 먼저 템플릿 데이터 마이그레이션 (세션스토리지 기반)
            for (const pUserId of pseudoUserIdsToMigrate) {
              console.log(`[SignupForm] 템플릿 데이터 마이그레이션 시작: ${pUserId} -> ${user.id}`);
              await migrateTemplateData(user.id, pUserId);
              console.log(`[SignupForm] 템플릿 데이터 마이그레이션 완료: ${pUserId} -> ${user.id}`);
            }

            // 2. DB 마이그레이션 (parsed_order 테이블 등) - 모든 작업이 완료될 때까지 기다림
            for (const pUserId of pseudoUserIdsToMigrate) {
              console.log(`[SignupForm] DB 마이그레이션 시작: ${pUserId} -> ${user.id}`);
              await migrateAllUserData(user.id, pUserId);
              console.log(`[SignupForm] DB 마이그레이션 완료: ${pUserId} -> ${user.id}`);
            }

            // 3. 마이그레이션이 완전히 완료됨을 명시
            localStorage.removeItem('migrationFlow'); // 마이그레이션 완료됨을 표시
            // console.log("[SignupForm] 모든 마이그레이션 완료, 플래그 제거");
            
            // toast.success('데이터가 성공적으로 이전되었습니다.', { id: 'migration' });
            
            // 초대 코드로 가입한 경우 모달 표시 플래그 설정
            if (hasReferralCode) {
              setShowReferralWelcomeModal(true)
            }
            
            // 완전히 마이그레이션이 끝난 후에 화면 이동
            // console.log("[SignupForm] Home.tsx로 이동");
            onComplete();
            
          } catch (migrationError) {
            console.error('[SignupForm] 데이터 마이그레이션 중 오류 발생:', migrationError);
            toast.error('일부 데이터 이전에 실패했습니다.', { id: 'migration' });
            
            // 마이그레이션 실패 시에도 플래그 제거 및 화면 이동
            localStorage.removeItem('migrationFlow');
            localStorage.removeItem('migrationFocusDate');
            console.log("[SignupForm] 마이그레이션 일부 실패했지만, Home.tsx로 이동");
            onComplete();
          }
        } catch (error) {
          // 이 부분은 회원가입 본체 프로세스(nickname 저장, 메타데이터 업데이트)에서
          // 발생한 오류를 처리합니다. 마이그레이션 자체 오류는 내부 try-catch에서 처리됩니다.
          console.error('[SignupForm] 회원가입 처리 중 오류 발생:', error);
          toast.error('회원가입 중 오류가 발생했습니다.', { id: 'migration' });
          setIsLoading(false);
        }
      } else {
        // 마이그레이션이 필요 없는 경우 바로 이동
        // 초대 코드로 가입한 경우 모달 표시 플래그 설정
        if (hasReferralCode) {
          setShowReferralWelcomeModal(true)
        }
        onComplete();
      }
    } catch (error) {
      logError('회원가입 중 오류가 발생했습니다:', error)
      toast.error('회원가입 중 오류가 발생했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* 닉네임 입력 */}
      <div className="space-y-2">
        <Label htmlFor="nickname">닉네임</Label>
        <Input
          id="nickname"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder="사용하실 닉네임을 입력해주세요"
          maxLength={20}
          required
        />
      </div>

      {/* 초대 코드 입력 (선택) */}
      <div className="space-y-2">
        <Label htmlFor="referralCode">
          초대 코드 <span className="text-gray-400 text-sm font-normal">(선택)</span>
        </Label>
        <Input
          id="referralCode"
          value={referralCode}
          onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
          placeholder="초대 코드가 있다면 입력해주세요"
          maxLength={8}
          className="uppercase"
        />
      </div>

      {/* 채널 정보 - 초대 코드 입력 시에만 표시 */}
      {hasReferralCode && (
        <>
          <div className="space-y-2">
            <Label>
              채널 플랫폼 <span className="text-red-500">*</span>
            </Label>
            <div className="flex flex-wrap gap-2">
              {CHANNEL_PLATFORMS.map((platform) => (
                <button
                  key={platform.value}
                  type="button"
                  onClick={() => setChannelPlatform(platform.value)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    channelPlatform === platform.value
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-brand-primary hover:text-brand-primary'
                  }`}
                >
                  {platform.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="channelLink">
              채널 링크/닉네임 <span className="text-red-500">*</span>
            </Label>
            <Input
              id="channelLink"
              value={channelLink}
              onChange={(e) => setChannelLink(e.target.value)}
              placeholder="예: @myChannel 또는 채널 URL"
            />
            <p className="text-xs text-gray-500">
              본인의 {channelPlatform ? CHANNEL_PLATFORMS.find(p => p.value === channelPlatform)?.label : '채널'} 채널 정보를 입력해주세요
            </p>
          </div>
        </>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? '처리중...' : '완료'}
      </Button>
    </form>
  )
}
